import { ref, watch, onScopeDispose, type Ref } from 'vue';
import { ANIMATION_CONFIG, GAME_CONFIG } from '@/config/constants';
import type { Letter } from '@/types/generated';

export interface UseLetterRevealQueueOptions {
  onQueueDrained?: () => void;
  /**
   * When this ref is `true` at the moment letters change, the current letters
   * are shown instantly with no stagger (reconnection restore). A live
   * selection leaves it `false` and animates. Must reflect the intent of the
   * snapshot that produced the letter change (set before the letters update).
   */
  revealInstantly?: Readonly<Ref<boolean>>;
}

export interface UseLetterRevealQueueReturn {
  visibleLetters: Readonly<Ref<Letter[]>>;
  isRevealing: Ref<boolean>;
  queueDrained: Ref<boolean>;
}

/**
 * Buffers incoming letters and reveals them sequentially with a stagger delay.
 *
 * Sits between raw snapshot props (allLetters) and the visible UI state.
 * Letters are revealed one at a time with at least LETTER_QUEUE_STAGGER_MS
 * between each, regardless of how fast they arrive.
 *
 * The pacing is enforced on a self-sustaining timer rather than on arrival
 * timing. The server now delivers auto-selected letters (bot / "pick for me" /
 * timer auto-fill) in a single `LettersBulkSelected` snapshot, so the reveal
 * cadence is owned entirely here, not by server/network message timing. Manual
 * single picks still arrive one snapshot at a time and reveal incrementally.
 * Whenever a reveal happens, the next one is gated behind a stagger timer, so
 * any burst of trickled-in snapshots is absorbed by that gate instead of racing.
 *
 * Reconnection restore is signalled explicitly via `options.revealInstantly`:
 * when true, the current letters are shown immediately with no animation.
 */
export function useLetterRevealQueue(
  allLetters: Ref<Letter[]>,
  options: UseLetterRevealQueueOptions = {}
): UseLetterRevealQueueReturn {
  const visibleLetters = ref<Letter[]>([]);
  const isRevealing = ref(false);
  const queueDrained = ref(false);

  let queuedCount = 0; // How many letters have been observed (committed to reveal)
  let staggerTimer: ReturnType<typeof setTimeout> | undefined; // paces reveals; while pending, arrivals wait for it
  let settleTimer: ReturnType<typeof setTimeout> | undefined; // lets the final letter's animation finish before going idle
  let disposed = false;

  const clearTimer = (timer: ReturnType<typeof setTimeout> | undefined) => {
    if (timer !== undefined) clearTimeout(timer);
  };

  const maybeDrain = (revealedCount: number) => {
    if (revealedCount >= GAME_CONFIG.TOTAL_LETTERS && !queueDrained.value) {
      queueDrained.value = true;
      options.onQueueDrained?.();
    }
  };

  // Reveal exactly one letter, then gate the next reveal behind a stagger.
  const revealNext = () => {
    if (disposed) return;
    staggerTimer = undefined;

    const currentAll = allLetters.value;
    if (visibleLetters.value.length >= currentAll.length) {
      // Nothing left to reveal right now. Let the final letter's reveal
      // animation play out before returning to idle, rather than cutting it.
      // Draining here (rather than the moment the tile becomes visible) gates
      // ReadyForFinding on the last flip finishing, so the round never advances
      // to word-building mid-reveal; see #430.
      clearTimer(settleTimer);
      settleTimer = setTimeout(() => {
        settleTimer = undefined;
        if (!disposed) {
          isRevealing.value = false;
          maybeDrain(visibleLetters.value.length);
        }
      }, ANIMATION_CONFIG.LETTER_REVEAL_DURATION);
      return;
    }

    const nextIndex = visibleLetters.value.length;
    visibleLetters.value = currentAll.slice(0, nextIndex + 1);
    isRevealing.value = true;

    // Always wait one stagger before the next reveal, even if the next letter
    // has not arrived yet. Snapshots that trickle in during this window are
    // absorbed by the gate below instead of revealing on arrival.
    staggerTimer = setTimeout(revealNext, ANIMATION_CONFIG.LETTER_QUEUE_STAGGER_MS);
  };

  // Start revealing if we are not already mid-stagger. When a stagger timer is
  // pending, a newly-observed letter is left for that timer to pick up, which
  // is what enforces the minimum spacing for back-to-back arrivals.
  const kick = () => {
    if (disposed || staggerTimer !== undefined) return;
    // Only a settle timer is pending, which means a full stagger has already
    // elapsed since the last reveal — safe to reveal the new letter now.
    clearTimer(settleTimer);
    settleTimer = undefined;
    revealNext();
  };

  watch(
    allLetters,
    letters => {
      const newCount = letters.length;

      if (newCount <= queuedCount) return; // No new letters

      // Reconnection restore: show the current letters instantly, no animation.
      // Drains (fires ReadyForFinding) only if the board is already complete.
      if (options.revealInstantly?.value) {
        clearTimer(staggerTimer);
        clearTimer(settleTimer);
        staggerTimer = undefined;
        settleTimer = undefined;
        visibleLetters.value = [...letters];
        queuedCount = newCount;
        isRevealing.value = false;
        maybeDrain(newCount);
        return;
      }

      queuedCount = newCount;
      kick();
    },
    { immediate: true }
  );

  onScopeDispose(() => {
    disposed = true;
    clearTimer(staggerTimer);
    clearTimer(settleTimer);
    staggerTimer = undefined;
    settleTimer = undefined;
  });

  return {
    visibleLetters,
    isRevealing,
    queueDrained,
  };
}
